import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOrderCounters } from '../store/ordersStore'

interface MenuSection {
  id: string
  title: string
  icon: string
  path?: string
  subItems?: {
    id: string
    title: string
    path: string
  }[]
}

interface HamburgerMenuProps {
  onClose?: () => void
}

const HamburgerMenu: React.FC<HamburgerMenuProps> = ({ onClose }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [expandedSections, setExpandedSections] = useState<string[]>([])
  const navigate = useNavigate()

  // Используем хук для загрузки счетчиков из глобального кеша напрямую
  const { counts } = useOrderCounters()

  const menuSections: MenuSection[] = [
    {
      id: 'catalog',
      title: 'Каталог',
      icon: 'file-tray-full',
      path: '/orders?category=catalog&subTab=new'
    },
    {
      id: 'views',
      title: 'Просмотры',
      icon: 'eye',
      subItems: [
        { id: 'watching', title: 'Просмотры', path: '/orders?category=views&subTab=watching' },
        { id: 'calculating', title: 'На просчете', path: '/orders?category=views&subTab=calculating' },
        { id: 'price_given', title: 'Оцененные', path: '/orders?category=views&subTab=price_given' }
      ]
    },
    {
      id: 'inwork',
      title: 'В работе',
      icon: 'construct',
      subItems: [
        { id: 'executing', title: 'Выполняются', path: '/orders?category=inwork&subTab=executing' },
        { id: 'defect', title: 'Брак', path: '/orders?category=inwork&subTab=defect' }
      ]
    },
    {
      id: 'archive',
      title: 'Архив',
      icon: 'archive',
      subItems: [
        { id: 'rejected', title: 'Отклоненные', path: '/orders?category=archive&subTab=rejected' },
        { id: 'archived', title: 'Архивные', path: '/orders?category=archive&subTab=archived' },
        { id: 'completed', title: 'Завершенные', path: '/orders?category=archive&subTab=completed' }
      ]
    }
  ]

  const toggleMenu = () => {
    setIsOpen(!isOpen)
  }

  const closeMenu = () => {
    setIsOpen(false)
    onClose?.()
  }

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      if (prev.includes(sectionId)) {
        // Если секция уже открыта - закрываем её
        return prev.filter(id => id !== sectionId)
      } else {
        // Если секция закрыта - закрываем все остальные и открываем эту
        return [sectionId]
      }
    })
  }

  const navigateToSection = (path: string) => {
    navigate(path)
    closeMenu()
  }

  return (
    <>
      {/* Кнопка гамбургер-меню */}
      <button 
        onClick={toggleMenu}
        className="flex items-center justify-center p-2 text-blue-600 hover:text-blue-700 transition-colors"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 12h18M3 6h18M3 18h18"/>
        </svg>
      </button>

      {/* Выпадающее меню */}
      {isOpen && (
        <div className="fixed inset-0 z-50">
          {/* Стеклянный оверлей */}
          <div 
            className="absolute inset-0 bg-white/20 backdrop-blur-md"
            onClick={closeMenu}
          />
          
          {/* Контент меню */}
          <div className="absolute top-16 left-4 w-64 bg-white/95 backdrop-blur-lg rounded-lg shadow-xl border border-white/20 overflow-hidden animate-in slide-in-from-left-2 duration-200">
            <div className="p-2">
              {menuSections.map((section) => (
                <div key={section.id} className="mb-1">
                  {/* Основной элемент секции */}
                  {section.path ? (
                    // Простая ссылка без подразделов
                    <button
                      onClick={() => navigateToSection(section.path!)}
                      className="w-full flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors text-left"
                    >
                      <div className="flex items-center space-x-2">
                        <ion-icon name={section.icon} class="text-lg text-blue-600"></ion-icon>
                        <span className="font-medium text-gray-900">{section.title}</span>
                      </div>
                    </button>
                  ) : (
                    // Секция с подразделами
                    <>
                      <button
                        onClick={() => toggleSection(section.id)}
                        className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors text-left"
                      >
                        <div className="flex items-center space-x-2">
                          <ion-icon name={section.icon} class="text-lg text-blue-600"></ion-icon>
                          <span className="font-medium text-gray-900">{section.title}</span>
                        </div>
                        <span className={`text-gray-400 transition-transform text-sm ${expandedSections.includes(section.id) ? 'rotate-180' : ''}`}>
                          ▼
                        </span>
                      </button>
                      
                      {/* Подразделы */}
                      {expandedSections.includes(section.id) && section.subItems && (
                        <div className="ml-4 mt-1 space-y-1">
                          {section.subItems.map((subItem) => (
                            <button
                              key={subItem.id}
                              onClick={() => navigateToSection(subItem.path)}
                              className="w-full flex items-center p-2 pl-8 rounded-lg hover:bg-gray-50 transition-colors text-left"
                            >
                              <span className="text-sm text-gray-700">{subItem.title}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default HamburgerMenu
